import { useStore } from '@/store';
import { computeVirtueFlawStats, computeXpBudget, validate } from '@/lib/budget';
import { InfoHint } from '@/components/ui/InfoHint';

export function BudgetSummary() {
  const c = useStore((s) => s.character!);
  const stats = computeVirtueFlawStats(c);
  const xp = computeXpBudget(c);
  const warnings = validate(c);

  const errs = warnings.filter((w) => w.severity === 'error');
  const warns = warnings.filter((w) => w.severity === 'warn');

  return (
    <div className="card text-sm space-y-3">
      <div className="font-display text-[11px] uppercase tracking-[0.28em] text-gold-400 flex items-center">
        Budget
        <InfoHint side="left">
          Live validation of your picks against the Definitive Edition rules.
          <br />
          <strong>Virtues</strong> / <strong>Flaws</strong> must balance.
          <strong>Minor/Story/Personality Flaws</strong> have per-type caps.
          <strong>XP</strong> shows Abilities + Arts spending vs. your total
          pool (apprenticeship + post-apprenticeship for magi; childhood + later
          life for others).
          <br />
          Red/amber items must be fixed before the character is legal.
        </InfoHint>
      </div>

      <div className="space-y-1">
        <Row
          label={`Virtues (${c.virtues.length})`}
          value={`+${stats.virtuePoints} / ${stats.budget}`}
          tone={stats.overBudget ? 'warn' : 'ok'}
        />
        <Row
          label={`Flaws (${c.flaws.length})`}
          value={`${stats.flawPoints}`}
          tone={stats.flawPoints > 0 ? 'warn' : 'muted'}
        />
        <Row
          label="Balanced"
          value={stats.balanced ? 'yes' : `off by ${stats.virtuePoints + stats.flawPoints}`}
          tone={stats.balanced ? 'ok' : 'warn'}
        />
        {c.type !== 'grog' && (
          <Row
            label="Minor Flaws"
            value={`${stats.minorFlawCount} / 5`}
            tone={stats.minorFlawCount > 5 ? 'warn' : 'muted'}
          />
        )}
        <Row
          label="Story Flaws"
          value={`${stats.storyFlaws} / 1`}
          tone={stats.storyFlaws > 1 ? 'warn' : 'muted'}
        />
        <Row
          label="Personality Flaws"
          value={`${stats.personalityFlaws} / 2`}
          tone={stats.personalityFlaws > 2 ? 'warn' : 'muted'}
        />
        <Row
          label="Social Status"
          value={stats.socialStatuses === 1 ? 'ok' : `${stats.socialStatuses}`}
          tone={stats.socialStatuses === 1 ? 'ok' : 'warn'}
        />
      </div>

      <div className="border-t border-gold-400/15 pt-2 space-y-1">
        <div className="font-display text-[11px] uppercase tracking-[0.28em] text-gold-400">
          XP
        </div>
        <Row
          label="Abilities + Arts"
          value={`${xp.spent} / ${xp.total}`}
          tone={xp.remaining < 0 ? 'warn' : 'muted'}
        />
        {xp.spellLevelPool && (
          <Row
            label="Spell levels"
            value={`${xp.spellLevelPool.spent} / ${xp.spellLevelPool.total}`}
            tone={xp.spellLevelPool.remaining < 0 ? 'warn' : 'muted'}
          />
        )}
      </div>

      {(errs.length > 0 || warns.length > 0) && (
        <div className="border-t border-gold-400/15 pt-2 space-y-1">
          {errs.map((w, i) => (
            <div key={`e${i}`} className="text-xs text-amber-300">
              ✗ {w.text}
            </div>
          ))}
          {warns.map((w, i) => (
            <div key={`w${i}`} className="text-xs text-parchment-300/80">
              ⚠ {w.text}
            </div>
          ))}
        </div>
      )}

      {c.type === 'magus' && (
        <div className="text-xs muted pt-2 border-t border-gold-400/15">
          Magi start with The Gift and Hermetic Magus (free, outside budget). Parma Magica,
          Magic Theory and Latin are Abilities, paid for from the 240 XP apprenticeship pool.
        </div>
      )}
    </div>
  );
}

function Row({ label, value, tone }: { label: string; value: string; tone: 'ok' | 'warn' | 'muted' }) {
  const cls =
    tone === 'warn' ? 'text-amber-300' : tone === 'ok' ? 'text-emerald-300' : 'muted';
  return (
    <div className="flex justify-between">
      <span className="muted">{label}</span>
      <span className={`font-semibold ${cls}`}>{value}</span>
    </div>
  );
}
